import type {
  AgenticLogicPublishGateKey,
  AgenticLogicRuleSettings,
} from "@intelligems/ig-types";
import type { AgenticLogicDataSurface } from "@intelligems/ig-utils";

import { useCodegenWorkspace } from "@src/features/agenticCodegen/workspace/hooks/useCodegenWorkspace";

import {
  type AgenticWorkspaceExtras,
  useAgenticApiPort,
} from "./agenticApiPort";
import { agenticBrainConfig } from "./agenticDomain";
import { AGENTIC_LOGIC_DEFAULT_TAB } from "./tabs";
import type {
  AgenticLogicTabKey,
  AgenticLogicWorkspaceViewModel,
} from "./types";

/**
 * The Agentic Logic binding of the shared code-workspace brain.
 *
 * Everything generic — the editor buffer, gate merging, publish gating, the
 * one-shot flush — lives in `useCodegenWorkspace`. Unlike CSP this binding adds
 * no domain-only surfaces, because the domain has no scenarios, capture window
 * or runs listener; it exists to supply the config and port and to pick the
 * default tab.
 */
export const useAgenticLogicRuleWorkspace = (
  ruleId: string | undefined,
  dataSurface: AgenticLogicDataSurface
): AgenticLogicWorkspaceViewModel & {
  ruleDescription: string | null;
  ruleSettings: AgenticLogicRuleSettings | null;
  repair: AgenticWorkspaceExtras["repair"];
  publishJob: AgenticWorkspaceExtras["publishJob"];
  publishOutcomePending: boolean;
} => {
  const { port, extras } = useAgenticApiPort(ruleId, dataSurface);

  const vm = useCodegenWorkspace<
    AgenticLogicPublishGateKey,
    AgenticLogicTabKey
  >(
    agenticBrainConfig(dataSurface),
    port,
    // Derived with the shell's own helper rather than named literally, so it
    // stays correct for free if a muted stub is ever listed ahead of Publish
    // gates again.
    AGENTIC_LOGIC_DEFAULT_TAB
  );

  return {
    ...vm,
    ruleDescription: extras.ruleDescription,
    ruleSettings: extras.ruleSettings,
    repair: extras.repair,
    publishJob: extras.publishJob,
    publishOutcomePending: extras.publishOutcomePending,
  };
};
